import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

import { updateClaimQueueAssignee } from '../../../store/participantService';
import { clearKeys } from '../../../utils/objectUtils';
import { hasRole, PRAP, QUALITY, SUPERUSER } from '../../../utils/userRoles';
import SingleSelect from '../../formElements/SingleSelect';
import FormActions from '../../ui/formActions/FormActions';

import { initialErrorState, validate } from './validateAssigneeEditor';

import form from '../../../commonStyles/formStyles.module.css';

const AssigneeEditor = ({ open, onAssignEditor, qualityOwners, rows }) => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        claimQueueIds: [],
        assigneeId: null
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [keys, setKeys] = useState({ assigneeId: '0' });
    const [errors, setErrors] = useState(initialErrorState);
    const acceptedRoles = [PRAP, QUALITY, SUPERUSER];
    const [isOpen, setIsOpen] = React.useState(false);
    const [isDisabled, setIsDisabled] = useState(false);

    // STORE STATE
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);

    // USE EFFECTS
    useEffect(() => {
        setIsOpen(open);
    }, [open]);

    useEffect(() => {
        if (successMessage === `Claim queues have been updated`) {
            clearData();
        }
    }, [successMessage]);

    // HELPER FNS
    const getPreSelect = (arr, id) => arr.find((el) => el.id === id) || {};

    const clearError = (key) => {
        setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    const clearData = () => {
        setNewEntry(initialState);
        setKeys(clearKeys(keys));
        setIsDisabled(false);
        onAssignEditor(false);
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        errors[key] && clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onModalClose = (event, reason) => {
        if (reason !== 'backdropClick') {
            setIsOpen(false);
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();

        const newErrors = validate(newEntry);

        if (Object.keys(newErrors)?.length > 0) {
            setErrors(newErrors);
            return;
        }

        const claimQueueIds = rows.filter((el) => el.checked === true).map((el) => el.id);
        setIsDisabled(true);
        const payload = {
            ...newEntry,
            claimQueueIds
        };
        dispatch(updateClaimQueueAssignee(payload));
    };

    // RENDER
    return (
        <Dialog
            className="muiDialogWrapperMedium"
            open={isOpen}
            onClose={onModalClose}
            aria-labelledby="form-dialog-title">
            <form onSubmit={onSubmit} data-testid="form_start" className={form.form}>
                <DialogTitle id="form-dialog-title">Assign Claim Item Assignee</DialogTitle>
                <DialogContent data-testid="dialog-content">
                    <SingleSelect
                        id={'assigneeId'}
                        key={keys.assigneeId}
                        label={'Claim Item Assignee'}
                        placeholder="Select claim item assignee..."
                        disabled={!hasRole(acceptedRoles, loggedInUser.roles)}
                        mandatory={true}
                        menuItems={qualityOwners}
                        selectedId={newEntry.assigneeId}
                        selected={getPreSelect(qualityOwners, newEntry.assigneeId)}
                        error={errors.assigneeId}
                        onChange={(chosenId) => onUpdate('assigneeId', chosenId)}
                    />
                </DialogContent>

                <DialogActions>
                    <FormActions
                        id="submit"
                        btnText="Update"
                        disabled={!hasRole(acceptedRoles, loggedInUser.roles) || isDisabled}
                        onClose={onSubmit}
                        onCancel={clearData}
                        customClass="noTopBorder"
                    />
                </DialogActions>
            </form>
        </Dialog>
    );
};

AssigneeEditor.propTypes = {
    onAssignEditor: PropTypes.func,
    open: PropTypes.bool.isRequired,
    qualityOwners: PropTypes.array.isRequired,
    rows: PropTypes.array.isRequired
};

export default AssigneeEditor;
